import { useState } from "react";
import { CANVAS_ID, isShapeTag } from "../../utils/svgParser";
import { EyeIcon, EyeOffIcon, LockIcon, LockOpenIcon, ChevronRightIcon, CanvasIcon, FolderIcon } from "./icons";

const TAG_ICON = {
  path: "✎",
  circle: "○",
  rect: "▭",
  ellipse: "◯",
  line: "╱",
  polygon: "⬠",
  polyline: "⌇",
  text: "T",
};

function ElementRow({ node, depth, selectedId, onSelect, onToggleVisibility, onToggleLock, collapsedIds, onToggleExpand }) {
  // tag non-shape (defs, style, linearGradient, clipPath, dll) tetap dirender di canvas
  // (Canvas.js gak filter apa pun), tapi gak masuk akal buat ditampilkan/diseleksi di sini
  if (!isShapeTag(node.tag)) return null;

  const hasChildren = !!node.children?.some((c) => isShapeTag(c.tag));
  const expanded = !collapsedIds.has(node.id);
  const isSelected = selectedId === node.id;

  return (
    <>
      <div
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        className={`w-full flex items-center gap-1 pr-2 text-sm transition-colors group ${
          isSelected ? "bg-[#2F8F6F]/30 text-[#D7C7A1]" : "text-[#D7C7A1]/70 hover:bg-[#145A43]/20 hover:text-[#D7C7A1]"
        }`}
      >
        <button
          onClick={() => hasChildren && onToggleExpand(node.id)}
          className={`w-4 h-4 flex items-center justify-center shrink-0 ${
            hasChildren ? "text-[#D7C7A1]/50 hover:text-[#D7C7A1]" : "opacity-0 pointer-events-none"
          }`}
        >
          {hasChildren && (
            <ChevronRightIcon className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
          )}
        </button>

        <button
          onClick={() => onSelect(node.id)}
          className="flex-1 flex items-center gap-2 py-1.5 min-w-0 text-left"
        >
          <span className="text-[#2F8F6F] w-4 text-center shrink-0 text-xs flex items-center justify-center">
            {node.tag === "g" ? <FolderIcon className="w-3.5 h-3.5" /> : (TAG_ICON[node.tag] || "•")}
          </span>
          <span className={`truncate ${node.hidden ? "opacity-40 line-through" : ""}`}>
            {node.name || node.id}
          </span>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock(node.id);
          }}
          title={node.locked ? "Buka kunci elemen" : "Kunci elemen"}
          className={`shrink-0 w-5 h-5 flex items-center justify-center rounded transition-opacity ${
            node.locked ? "opacity-100 text-[#D7C7A1]/50" : "opacity-0 group-hover:opacity-100 text-[#D7C7A1]/70"
          } hover:!opacity-100 hover:text-[#2F8F6F]`}
        >
          {node.locked ? <LockIcon className="w-3.5 h-3.5" /> : <LockOpenIcon className="w-3.5 h-3.5" />}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(node.id);
          }}
          title={node.hidden ? "Tampilkan elemen" : "Sembunyikan elemen"}
          className={`shrink-0 w-5 h-5 flex items-center justify-center rounded transition-opacity ${
            node.hidden ? "opacity-100 text-[#D7C7A1]/40" : "opacity-0 group-hover:opacity-100 text-[#D7C7A1]/70"
          } hover:!opacity-100 hover:text-[#2F8F6F]`}
        >
          {node.hidden ? <EyeOffIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
        </button>
      </div>

      {hasChildren && expanded && (
        <div className="border-l border-[#145A43]/30 ml-[19px]">
          {node.children.map((child) => (
            <ElementRow
              key={child.id}
              node={child}
              depth={depth}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggleVisibility={onToggleVisibility}
              onToggleLock={onToggleLock}
              collapsedIds={collapsedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default function ElementsPanel({ tree, selectedId, onSelect, onToggleVisibility, onToggleLock, onAddImport }) {
  const [collapsedIds, setCollapsedIds] = useState(new Set());

  const toggleExpand = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddImportFile = (e) => {
    const file = e.target.files[0];
    if (file) onAddImport(file);
    e.target.value = "";
  };

  return (
    <div className="w-64 shrink-0 bg-[#0F0F0F] border-r border-[#145A43]/40 flex flex-col">
      <div className="px-4 py-3 border-b border-[#145A43]/40 flex items-center justify-between">
        <h2 className="text-[#D7C7A1] text-sm font-medium">Elements</h2>
        <label
          title="Import SVG tambahan"
          className="text-xs text-[#2F8F6F] hover:text-[#3aa87f] cursor-pointer flex items-center gap-1"
        >
          + SVG
          <input type="file" accept=".svg,image/svg+xml" className="hidden" onChange={handleAddImportFile} />
        </label>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        <button
          onClick={() => onSelect(CANVAS_ID)}
          className={`w-full flex items-center gap-2 px-4 py-1.5 text-sm font-medium transition-colors ${
            selectedId === CANVAS_ID
              ? "bg-[#2F8F6F]/40 text-[#D7C7A1]"
              : "text-[#D7C7A1]/90 hover:bg-[#145A43]/20"
          }`}
        >
          <CanvasIcon className="w-4 h-4 text-[#2F8F6F]" />
          Canvas
        </button>

        {tree.length === 0 && (
          <p className="px-4 pt-2 text-xs text-[#D7C7A1]/40">Belum ada elemen. Klik "+ SVG" buat nambah.</p>
        )}

        <div className="border-l border-[#145A43]/30 ml-[18px] mt-1">
          {tree.map((node) => (
            <ElementRow
              key={node.id}
              node={node}
              depth={0}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggleVisibility={onToggleVisibility}
              onToggleLock={onToggleLock}
              collapsedIds={collapsedIds}
              onToggleExpand={toggleExpand}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
